import { LinkText } from '@/components/ui/link-text';
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import type { AnalyticsEvents } from '@/features/analytics/types/analytics-events';
import { Dialog } from '@/features/dialog/dialog';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import { useDrawers } from '@/features/drawers/all-drawers';
import { getFullAddressString } from '@/lib/address-helper';
import { useAppState } from '@/lib/hooks/use-app-state';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerBasics } from '@/server/types/infer-trpc-types';
import { api } from '@/utils/api';
import { getFullName } from '@/utils/name';
import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { EventFormData } from './event-form-fields';
import { EventFormFields } from './event-form-fields';

export interface EventCreateDialogProps {
    analyticsSource: AnalyticsEvents['event_created']['source'];
    customer?: CustomerBasics;
    date?: string;
    onCreated?: () => void;
}

export const EventCreateDialog = DialogStateManager.create<EventCreateDialogProps>(
    ({ customer, date, onCreated, analyticsSource }) => {
        const { openEventDetails } = useDrawers();
        const customerId = customer?.customerId;
        const { t } = useLocale('events');
        const { toast } = useToast();
        const { visible, remove } = useDialog();
        const appState = useAppState();
        const apiUtils = api.useUtils();
        const sendEvent = useAnalytics();

        const defaultValues = useMemo<EventFormData>(
            () => ({
                customer_id: customerId || '',
                date: date ?? formatISO(new Date(), { representation: 'date' }),
                address: (customer && getFullAddressString(customer.address)) || '',
                recurrence: '',
            }),
            [customerId, customer, date],
        );

        const form = useForm<EventFormData>({
            defaultValues,
            mode: 'onBlur',
        });

        const {
            control,
            handleSubmit,
            formState: { isValid, errors },
        } = form;

        const createEventMutation = api.platoUser.command.events.create.useMutation({
            onSuccess({ event_id }, { date, recurrence, start_time, end_time, customer_id }) {
                toast({
                    icon: 'success',
                    title: t('events__form_create_toast__success_title'),
                    createdLink: <LinkText as="button" onClick={() => openEventDetails(event_id, date)} />,
                });

                void apiUtils.platoUser.query.events.getList.invalidate();
                void apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                    customerId: customer_id,
                });

                sendEvent('event_created', {
                    is_recurring: !!recurrence,
                    has_set_time: !!start_time && !!end_time,
                    source: analyticsSource,
                });

                remove();
                onCreated?.();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const onSubmit = (data: EventFormData) => {
            createEventMutation.mutate(data);
        };

        if (!appState.platoUser) {
            return null;
        }

        return (
            <Dialog open={visible} onClose={remove} showClose>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header title={t('events__form_create_title')} />
                    <DialogForm.Content>
                        <EventFormFields
                            form={form}
                            ownerName={getFullName(appState.platoUser.firstName, appState.platoUser.lastName)}
                            customer={customer}
                        />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={remove}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={createEventMutation.isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
